import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import Breadcrumbs from "@/components/Breadcrumbs";
import { SmartBackLink } from "@/components/SmartBackLink";
import { getNationalEventsRange, type NationalEventsRangeData, type PoliceEvent } from "@/lib/polisen.functions";
import { getCrimeTypeColor } from "@/lib/crime-colors";
import { crimeTypeToSlug } from "@/lib/crime-slug";
import { EventTimePair } from "@/components/EventTimePair";
import { LastUpdated } from "@/components/LastUpdated";
import { canonical, DEFAULT_OG_IMAGE, jsonLdScript, SITE_NAME, getCurrentYear, pageTitle} from "@/lib/seo";
import ShareButton from "@/components/ShareButton";
import { eventHasLandingPath, eventLandingLinkProps } from "@/lib/event-map-link";
import { EventMapButton } from "@/components/EventMapButton";
import NationalNav from "@/components/NationalNav";
import NationalFooterLinks from "@/components/NationalFooterLinks";
import { CrimeTypeIcon } from "@/components/CrimeTypeIcon";
import { EventsToolbar, applyEventsFilter, type EventsSort } from "@/components/EventsToolbar";
import { ContextValuePanel } from "@/components/ContextValuePanel";
import { AdSlot } from "@/components/AdSlot";

const DAYS = 7;

export const Route = createFileRoute("/senaste-veckan")({
  head: () => {
    const year = getCurrentYear();
    const url = canonical("/senaste-veckan");
    const title = pageTitle(`Blåljus senaste veckan – polishändelser i Sverige ${year}`);
    const description = `Polishändelser och blåljus i hela Sverige senaste 7 dagarna ${year}. Färska brottsrapporter, brottstyper och plats – uppdateras dagligen.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { property: "og:image", content: DEFAULT_OG_IMAGE },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: DEFAULT_OG_IMAGE },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: import.meta.env.SSR ? [
        jsonLdScript({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Hem", item: canonical("/") },
            { "@type": "ListItem", position: 2, name: "Hela landet", item: canonical("/hela-landet") },
            { "@type": "ListItem", position: 3, name: "Senaste veckan", item: url },
          ],
        }),
        jsonLdScript({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "@id": `${url}#webpage`,
          url,
          name: title,
          description,
          inLanguage: "sv-SE",
          isPartOf: { "@id": "https://kollabrotten.se/#website" },
          about: { "@type": "Place", name: "Sverige" },
          primaryImageOfPage: { "@type": "ImageObject", url: DEFAULT_OG_IMAGE },
          temporalCoverage: "P7D",
        }),
      ] : [],
    };
  },
  loader: async () => {
    return await getNationalEventsRange({ data: { daysBack: DAYS } });
  },
  component: NationalRecentPage,
  errorComponent: ({ error }) => (
    <div className="mx-auto max-w-7xl px-4 py-20 text-center sm:px-6 lg:px-8">
      <h1 className="text-4xl font-bold text-foreground">Något gick fel</h1>
      <p className="mt-2 text-muted-foreground">{(error as Error)?.message}</p>
    </div>
  ),
});

function NationalRecentPage() {
  const data = Route.useLoaderData() as NationalEventsRangeData;
  const PAGE_SIZE = 25;
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<EventsSort>("newest");
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set());
  const typeOptions = useMemo(
    () => data.crimeBreakdown.map((b) => ({ type: b.type, count: b.count })),
    [data.crimeBreakdown],
  );
  const filtered = useMemo(
    () => applyEventsFilter(data.events, search, selectedTypes, sort, typeOptions.length),
    [data.events, search, selectedTypes, sort, typeOptions.length],
  );
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pagedEvents = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const today = data.events.filter(
    (e) => Date.now() - new Date(e.datetime).getTime() < 24 * 60 * 60 * 1000,
  ).length;
  const topType = data.crimeBreakdown[0];

  // Per-dygnsfördelning de senaste 7 dygnen (idag = sista bucketen)
  const dayBuckets = Array.from({ length: DAYS }, (_, i) => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - (DAYS - 1 - i));
    return { date: d, count: 0 };
  });
  for (const e of data.events) {
    const t = new Date(e.datetime).getTime();
    for (let i = 0; i < dayBuckets.length; i++) {
      const start = dayBuckets[i].date.getTime();
      const end = start + 24 * 60 * 60 * 1000;
      if (t >= start && t < end) {
        dayBuckets[i].count += 1;
        break;
      }
    }
  }
  const maxDay = Math.max(1, ...dayBuckets.map((b) => b.count));
  const avgPerDay = Math.round(data.totalIncidents / DAYS);
  const topTypes = data.crimeBreakdown.slice(0, 6);
  const dayNames = ["Sön", "Mån", "Tis", "Ons", "Tor", "Fre", "Lör"];

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-3"><SmartBackLink to="/">Tillbaka</SmartBackLink></div>
      <Breadcrumbs
        items={[
          { label: "Hem", to: "/" },
          { label: "Hela landet", to: "/hela-landet" },
          { label: "Senaste veckan" },
        ]}
      />

      <NationalNav active="senaste-veckan" />

      <div className="mb-6 flex flex-wrap items-center justify-end gap-3">
        <ShareButton
          title={`Senaste veckans händelser i Sverige | ${SITE_NAME}`}
          text="Färska polishändelser i hela Sverige senaste 7 dagarna"
          className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm font-semibold text-foreground hover:border-primary hover:bg-muted hover:text-primary"
        />
      </div>

      <div className="mb-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <h1 className="text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
            Blåljus senaste veckan i Sverige
          </h1>
          <LastUpdated datetime={data.events?.[0]?.datetime ?? null} />
        </div>
        <p className="mt-3 max-w-2xl text-sm text-muted-foreground leading-relaxed">
          Alla polishändelser i <strong className="text-foreground">hela Sverige</strong> som
          rapporterats de senaste 7 dagarna — sorterade från nyaste till äldsta. Färska rapporter
          direkt från Polisens öppna API.
        </p>
      </div>

      <ContextValuePanel variant="national-week" className="mb-6" />

      <AdSlot variant="horizontal" reserveSpace className="mb-6" />

      <div className="stat-card-group mb-8 grid gap-2 grid-cols-2 sm:grid-cols-4">
        <div className="stat-card min-w-0">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Senaste 7 dagarna</p>
          <p className="mt-1 text-2xl sm:text-3xl font-bold text-foreground">{data.totalIncidents.toLocaleString("sv-SE")}</p>
          <p className="mt-1 text-xs text-muted-foreground">polishändelser i Sverige</p>
        </div>
        <div className="stat-card min-w-0">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Senaste 24 timmarna</p>
          <p className="mt-1 text-2xl sm:text-3xl font-bold text-foreground">{today}</p>
          <p className="mt-1 text-xs text-muted-foreground">nya rapporter idag</p>
        </div>
        <div className="stat-card min-w-0">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Snitt per dygn</p>
          <p className="mt-1 text-2xl sm:text-3xl font-bold text-foreground">{avgPerDay.toLocaleString("sv-SE")}</p>
          <p className="mt-1 text-xs text-muted-foreground">rapporter/dygn senaste veckan</p>
        </div>
        <div className="stat-card min-w-0">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Vanligaste typen</p>
          <p className="mt-1 text-base sm:text-xl font-bold text-foreground break-words">{topType?.type ?? "-"}</p>
          <p className="mt-1 text-xs text-muted-foreground">{topType?.count ?? 0} rapporter denna vecka</p>
        </div>
      </div>

      <section className="mb-8 grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="mb-3 flex items-baseline justify-between">
            <h2 className="text-base font-bold text-foreground">Händelser per dygn</h2>
            <span className="text-xs text-muted-foreground">Senaste 7 dygnen</span>
          </div>
          <div className="flex items-end justify-between gap-2 h-40">
            {dayBuckets.map((b, i) => {
              const h = Math.max(4, Math.round((b.count / maxDay) * 140));
              const isToday = i === dayBuckets.length - 1;
              return (
                <div key={i} className="flex flex-1 flex-col items-center gap-1 min-w-0">
                  <span className="text-[11px] font-semibold tabular-nums text-foreground">{b.count}</span>
                  <div
                    className={
                      "w-full rounded-t " +
                      (isToday ? "bg-primary" : "bg-primary/40")
                    }
                    style={{ height: `${h}px` }}
                    aria-label={`${b.count} händelser ${b.date.toLocaleDateString("sv-SE")}`}
                  />
                  <span className="text-[10px] text-muted-foreground">
                    {dayNames[b.date.getDay()]}
                  </span>
                  <span className="text-[10px] text-muted-foreground tabular-nums">
                    {b.date.getDate()}/{b.date.getMonth() + 1}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-4">
          <div className="mb-3 flex items-baseline justify-between">
            <h2 className="text-base font-bold text-foreground">Vanligaste brottstyperna</h2>
            <Link to="/rikstatistik/brottstyper" className="text-xs font-semibold text-primary hover:underline">
              Alla brottstyper →
            </Link>
          </div>
          <ul className="space-y-2">
            {topTypes.map((t) => (
              <li key={t.type}>
                <Link
                  to="/rikstatistik/brottstyp/$type"
                  params={{ type: crimeTypeToSlug(t.type) }}
                  className="group block"
                >
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-foreground group-hover:text-primary truncate">
                      {t.type}
                    </span>
                    <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                      {t.count.toLocaleString("sv-SE")} st · {t.pct}%
                    </span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded bg-muted">
                    <div
                      className="h-full bg-primary"
                      style={{ width: `${Math.max(2, t.pct)}%` }}
                    />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mb-8 rounded-lg border border-border bg-card p-4">
        <h2 className="text-base font-bold text-foreground mb-3">Fördjupa dig</h2>
        <div className="flex flex-wrap gap-2">
          <Link to="/karta" search={{}} className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold text-foreground hover:border-primary hover:text-primary">
            🗺️ Öppna Sverigekartan
          </Link>
          <Link to="/dagssammanfattning/hela-sverige" className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold text-foreground hover:border-primary hover:text-primary">
            📅 Dagens händelser i Sverige
          </Link>
          <Link to="/nara-mig" className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold text-foreground hover:border-primary hover:text-primary">
            📍 Nära mig
          </Link>
          <Link to="/kommuner" className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold text-foreground hover:border-primary hover:text-primary">
            🏙️ Alla kommuner
          </Link>
        </div>
      </section>

      <AdSlot variant="in-article" className="mb-8" />

      {data.topCityCounts.length > 0 && (
        <section className="mb-8">
          <div className="mb-3">
            <h2 className="text-lg font-bold text-foreground">Mest aktiva kommunerna senaste 7 dagarna</h2>
            <p className="text-xs text-muted-foreground">Topp 15 kommuner med flest polisrapporterade händelser.</p>
          </div>
          <ul className="divide-y divide-border rounded-lg border border-border bg-card overflow-hidden">
            {data.topCityCounts.map((c) => (
              <li key={c.citySlug} className="flex items-center justify-between gap-3 px-4 py-2.5">
                <Link to="/kommun/$city" params={{ city: c.citySlug }} className="text-sm font-semibold text-primary hover:underline truncate">
                  {c.cityName}
                </Link>
                <span className="shrink-0 tabular-nums text-sm text-muted-foreground">{c.count} st</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {data.events.length === 0 ? (
        <p className="text-sm text-muted-foreground">Inga polishändelser de senaste 7 dagarna.</p>
      ) : (
        <section>
          <div className="mb-3">
            <h2 className="text-lg font-bold text-foreground">Senaste händelserna i Sverige</h2>
          </div>
          <EventsToolbar
            search={search}
            onSearchChange={(v) => { setSearch(v); setPage(1); }}
            searchPlaceholder="Sök bland veckans rapporter..."
            sort={sort}
            onSortChange={(v) => { setSort(v); setPage(1); }}
            types={typeOptions}
            selected={selectedTypes}
            onSelectedChange={(next) => { setSelectedTypes(next); setPage(1); }}
          />
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            {pagedEvents.map((incident: PoliceEvent, incidentIndex: number) => {
              const hasMap = eventHasLandingPath(incident);
              const wrapClass =
                "flex items-stretch border-b border-border last:border-b-0";
              const rowClass =
                "relative block flex-1 min-w-0 px-3 py-3 pr-12 sm:px-4 sm:pr-14 hover:bg-muted/40 transition-colors";
              const citySlug = (incident as any).city_slug as string | null;
              const cityName = citySlug
                ? ((incident.name?.split(",").pop() ?? citySlug).trim())
                : null;
              const inner = (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 flex-1 items-start gap-2.5">
                      <CrimeTypeIcon type={incident.type} className="h-8 w-8 shrink-0" />
                      <h3 className="min-w-0 text-base font-semibold text-foreground leading-snug line-clamp-2">
                        {incident.summary || incident.name}
                      </h3>
                    </div>
                    <div className="shrink-0">
                      <EventTimePair
                        eventAt={incident.datetime}
                        receivedAt={(incident as any).created_at}
                        eventText={incident.name}
                      />
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className={`badge-crime text-[11px] ${getCrimeTypeColor(incident.type)}`}>{incident.type}</span>
                    {incident.place_label && (
                      <span className="text-xs text-muted-foreground">📍 {incident.place_label}</span>
                    )}
                    {cityName && (
                      <span className="text-xs font-medium text-muted-foreground">{cityName}</span>
                    )}
                  </div>
                </>
              );
              return (
                <div key={incident.id}>
                  {incidentIndex > 0 && incidentIndex % 8 === 0 && (
                    <div className="border-b border-border px-3 py-3 sm:px-4">
                      <AdSlot variant="in-feed" />
                    </div>
                  )}
                  <div className={wrapClass}>
                  {hasMap ? (
                    <a {...eventLandingLinkProps(incident)!} className={`group cursor-pointer ${rowClass}`}>
                      {inner}
                    </a>
                  ) : (
                    <div className={rowClass}>{inner}</div>
                  )}
                  <EventMapButton event={incident} city={citySlug} />
                  </div>
                </div>
              );
            })}
          </div>
          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-center gap-2">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage === 1} className="rounded-md border border-border bg-card px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50">← Föregående</button>
              <span className="text-sm text-muted-foreground">Sida {safePage} av {totalPages}</span>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage === totalPages} className="rounded-md border border-border bg-card px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50">Nästa →</button>
            </div>
          )}
        </section>
      )}
      <NationalFooterLinks currentTo="/senaste-veckan" />
    </div>
  );
}